import { useMemo } from "react";
import { useTranslation } from "@repo/core";
import type { LegalDocument } from "../content/legal/types";
import privacyPolicyEn from "../content/legal/privacyPolicy.en";
import privacyPolicyCs from "../content/legal/privacyPolicy.cs";
import termsEn from "../content/legal/termsAndConditions.en";
import termsCs from "../content/legal/termsAndConditions.cs";

export type LegalDocumentId = "privacyPolicy" | "termsAndConditions";

/**
 * Legal documents are kept as structured content modules rather than i18next
 * strings: their wording is binding and must stay verbatim, so each language
 * has its own module generated from the source Word document. Only the
 * surrounding page chrome goes through i18next.
 */
const DOCUMENTS: Record<LegalDocumentId, { en: LegalDocument; cs: LegalDocument }> = {
  privacyPolicy: { en: privacyPolicyEn, cs: privacyPolicyCs },
  termsAndConditions: { en: termsEn, cs: termsCs },
};

/** Returns the legal document variant matching the active UI language. */
export const useLegalDocument = (id: LegalDocumentId): LegalDocument => {
  const { i18n } = useTranslation();

  return useMemo(() => {
    const variants = DOCUMENTS[id];
    return i18n.language?.toLowerCase().startsWith("cs") ? variants.cs : variants.en;
  }, [id, i18n.language]);
};

export default useLegalDocument;
